import { useStore } from '@nanostores/react';
import { getLocalTimeZone, today } from '@internationalized/date';
import { Box, Heading, Link, Paragraph, Stack, Strong, useModals } from '@customerio/pluma-components/react';
import { billing } from '@/services/billing';
import { PlanType } from '@/services/billing/plans';
import type { BillingPlan } from '@/services/billing/types';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { formatCentsToUsd } from '@/utils/currency';
import { formatNumberAndPluralize } from '@/utils/number';
import { parseDateLoosely } from '@/utils/time';
import type { BillingEstimates } from '@/services/billing/types';
import { CreditCardForm, type CheckoutFormOptions } from './credit-card-form';
import type { CreditCardFormAddress, CreditCardSubmitResult } from '../utils/credit-card-form-submit';
import { type CheckoutTotals, computeCheckoutTotals } from '../utils/checkout-totals';
import { PlanIncludedModal } from './plan-included-modal';
import { useTaxRateRefresh } from '../hooks/use-tax-rate-refresh';

/** The coupon fields the checkout summary reads (Stripe and Ordway coupons both carry these). */
export type CheckoutCoupon = {
  name?: string;
  amount_off?: number | null;
  percent_off?: number | null;
  expiration_date?: string | null;
};

type SignupFormProps = {
  /** The plan being purchased (priced in the summary). */
  plan: BillingPlan;
  /** The applied coupon, if any. */
  coupon?: CheckoutCoupon;
  /** Prefilled coupon code. */
  couponCode?: string | null;
  /** Completes the purchase with the confirmed card once the form is submitted. */
  startSubscription: (result: CreditCardSubmitResult) => Promise<void>;
  /** Whether to show the "Back to plans" button. */
  canCancel?: boolean;
  cancelActionLabel?: string | null;
  /** Whether the coupon-entry field is shown (the Ordway coupon flow). */
  isAllowedToUseCoupon?: boolean;
  onApplyCoupon?: (code: string, address: CreditCardFormAddress) => void;
  /** Whether the price is being recomputed (e.g. a coupon is being applied). */
  isPriceLoading?: boolean;
  /** Account usage counts for the Essentials overage copy (0 when unknown). */
  usage?: { customerCount: number; objectCount: number };
};

/** Whether the plan's checkout shows the Essentials-style base-cost + usage-overage copy. */
function isUsageBillingPlan(planType: PlanType): boolean {
  return planType === PlanType.Essentials || planType === PlanType.Startup;
}

/**
 * Whether to show the premium-tier intro (the "What's included in my plan?" link / future-start
 * note): Enterprise, or any plan flagged `premium` on the wire model. Ports the Ember `(eq planType
 * 'enterprise') or plan.premium` condition, kept inclusive of the Premium plan type.
 */
function isPremiumTierPlan(plan: BillingPlan): boolean {
  if (plan.planType === PlanType.Premium || plan.planType === PlanType.Enterprise) {
    return true;
  }
  // `premium` is a raw wire field on Stripe plans, not on the shared BillingPlan shape.
  // oxlint-disable typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return (
    'premium' in plan &&
    (
      plan as {
        premium?: boolean;
      }
    ).premium === true
  );
  // oxlint-enable typescript/consistent-type-assertions
}

/**
 * Whether the next bill will be overages-only: the coupon still covers the plan past the next billing
 * date, and nothing is due today. Ports the Ember `isNextBillOveragesOnly`.
 */
export function isNextBillOveragesOnly(
  coupon: CheckoutCoupon | undefined,
  dueToday: number | null,
  total: number,
): boolean {
  if (coupon?.expiration_date == null || coupon.expiration_date === '') {
    return false;
  }
  if (dueToday != null && dueToday > 0) {
    return false;
  }
  if (total > 0) {
    return false;
  }
  // A coupon expiration date this can't parse degrades to "not overages-only" (the same as a missing
  // one above), rather than throwing — see `parseDateLoosely`'s doc comment.
  const expirationDate = parseDateLoosely(coupon.expiration_date);
  if (expirationDate == null) {
    return false;
  }
  const oneMonthFromNow = today(getLocalTimeZone()).add({ months: 1 });
  return expirationDate.compare(oneMonthFromNow) > 0;
}

/** The customer/object overage amounts (cents) from the billing estimate, and whether to show them. */
function readOverages(estimates: BillingEstimates | undefined): {
  customerOverage: number;
  objectOverage: number;
  showOverages: boolean;
} {
  const customerOverage = estimates?.customer_overage ?? 0;
  const objectOverage = estimates?.object_overage ?? 0;
  return { customerOverage, objectOverage, showOverages: customerOverage > 0 || objectOverage > 0 };
}

type BuildCheckoutOptionsParams = {
  coupon?: CheckoutCoupon;
  couponCode?: string | null;
  isAllowedToUseCoupon?: boolean;
  isPriceLoading?: boolean;
  startDateInFuture: boolean;
  isMonthlyInterval: boolean;
  totals: CheckoutTotals;
  canCancel?: boolean;
  cancelActionLabel?: string | null;
  onApplyCoupon?: (code: string, address: CreditCardFormAddress) => void;
  onBillingAddressChange: (address: CreditCardFormAddress, changedField: keyof CreditCardFormAddress) => void;
  isTaxRateLoading: boolean;
};

/** Assembles the checkout extras passed to the credit-card form from the priced summary + handlers. */
function buildCheckoutOptions(params: BuildCheckoutOptionsParams): CheckoutFormOptions {
  return {
    appliedCouponName: params.coupon?.name,
    couponCode: params.couponCode ?? undefined,
    showCouponInput: params.isAllowedToUseCoupon,
    isApplyingCoupon: params.isPriceLoading,
    onApplyCoupon: params.onApplyCoupon,
    isPriceLoading: params.isPriceLoading,
    showTotalDue: !params.startDateInFuture,
    total: params.totals.total,
    dueToday: params.totals.dueToday,
    dueTodayPreSavings: params.totals.dueTodayPreSavings,
    isMonthlyInterval: params.isMonthlyInterval,
    isNextBillOveragesOnly: isNextBillOveragesOnly(params.coupon, params.totals.dueToday, params.totals.total),
    canCancel: params.canCancel,
    cancelActionLabel: params.cancelActionLabel ?? undefined,
    onBillingAddressChange: params.onBillingAddressChange,
    isTaxRateLoading: params.isTaxRateLoading,
  };
}

type OverageDetailsProps = {
  customerCount: number;
  objectCount: number;
  customerOverage: number;
  objectOverage: number;
};

/** The per-usage overage lines shown for Essentials accounts already over their allowances. */
function OverageDetails({ customerCount, objectCount, customerOverage, objectOverage }: OverageDetailsProps) {
  return (
    <>
      {customerOverage > 0 ? (
        <Paragraph data-test-essentials-signup-form-contact-overage-details>
          Your account currently has <Strong>{formatNumberAndPluralize(customerCount, 'contact')}</Strong>, resulting in
          an additional usage cost of <Strong>{formatCentsToUsd(customerOverage)}</Strong> per month.
        </Paragraph>
      ) : null}
      {objectOverage > 0 ? (
        <Paragraph data-test-essentials-signup-form-object-overage-details>
          Your account currently has <Strong>{formatNumberAndPluralize(objectCount, 'object')}</Strong>, resulting in an
          additional usage cost of <Strong>{formatCentsToUsd(objectOverage)}</Strong> per month.
        </Paragraph>
      ) : null}
    </>
  );
}

type SignupHeaderProps = {
  plan: BillingPlan;
  startDateInFuture: boolean;
  /** The formatted subscription start date, shown when the start date is in the future. */
  formattedStartDate: string;
  planPrice: number;
  showOverages: boolean;
  usage: { customerCount: number; objectCount: number };
  customerOverage: number;
  objectOverage: number;
  onWhatsIncluded: () => void;
};

/** The signup form's heading and plan-specific intro copy (overage details or "what's included"). */
function SignupHeader({
  plan,
  startDateInFuture,
  formattedStartDate,
  planPrice,
  showOverages,
  usage,
  customerOverage,
  objectOverage,
  onWhatsIncluded,
}: SignupHeaderProps) {
  const title = startDateInFuture ? 'Add a payment method to start your subscription' : 'Start your subscription';
  const futureStartText =
    formattedStartDate === ''
      ? "You won't be billed yet."
      : `You won't be billed yet. Your first payment date will be the ${formattedStartDate}.`;

  return (
    <Box mb="300">
      <Heading level="2" data-test-signup-form-header>
        {title}
      </Heading>
      {isUsageBillingPlan(plan.planType) && showOverages ? (
        <Stack gap="100" mt="150">
          <Paragraph>Your base plan cost is {formatCentsToUsd(planPrice)} per month.</Paragraph>
          <OverageDetails
            customerCount={usage.customerCount}
            objectCount={usage.objectCount}
            customerOverage={customerOverage}
            objectOverage={objectOverage}
          />
        </Stack>
      ) : null}
      {isPremiumTierPlan(plan) ? (
        <Box mt="150">
          {startDateInFuture ? (
            <Paragraph data-test-pinned-start-date-text>{futureStartText}</Paragraph>
          ) : (
            <Link data-test-included-in-plan-modal onClick={onWhatsIncluded}>
              What's included in your plan?
            </Link>
          )}
        </Box>
      ) : null}
    </Box>
  );
}

/**
 * The checkout signup form: a plan-priced summary header and the credit-card form that completes the
 * subscription. Ports the Ember `signup-form`. Reads the tax rate + usage overages from the billing
 * service to price the plan, and refreshes the tax rate as the billing address changes.
 */
type SignupPricing = {
  startDateInFuture: boolean;
  formattedStartDate: string;
  totals: CheckoutTotals;
  showOverages: boolean;
  customerOverage: number;
  objectOverage: number;
  isMonthlyInterval: boolean;
  newPipelinesObjectsPricing: boolean;
};

/** Reads the billing state (tax rate, usage estimates, plans) and prices the plan for the summary. */
function useSignupPricing(plan: BillingPlan, coupon: CheckoutCoupon | undefined): SignupPricing {
  // Subscribe to the subscription atom so the time-relative `startDateInFuture` getter recomputes.
  useStore(billing.subscription);
  const formattedStartDate = useStore(billing.formattedStartDate);
  const taxRate = useStore(billing.taxRate);
  const estimates = useStore(billing.billingEstimates);
  const pinnedPlan = useStore(billing.pinnedPlan);
  const essentialsPlan = useStore(billing.essentialsPlan);
  const newPipelinesObjectsPricing = useFeatureFlag(AccountFeatureFlag.NewPipelinesObjectsPricing);

  const { customerOverage, objectOverage, showOverages } = readOverages(estimates);

  return {
    startDateInFuture: billing.startDateInFuture,
    formattedStartDate,
    totals: computeCheckoutTotals({
      plan,
      coupon,
      taxRatePercent: taxRate?.rate,
      customerOverageCents: customerOverage,
      objectOverageCents: objectOverage,
    }),
    showOverages,
    customerOverage,
    objectOverage,
    // A monthly pinned plan, or any account with an Essentials plan available (Ember's isMonthlyInterval).
    isMonthlyInterval: pinnedPlan?.interval_name === 'month' || essentialsPlan != null,
    newPipelinesObjectsPricing,
  };
}

export function SignupForm({
  plan,
  coupon,
  couponCode,
  startSubscription,
  canCancel,
  cancelActionLabel,
  isAllowedToUseCoupon,
  onApplyCoupon,
  isPriceLoading,
  usage = { customerCount: 0, objectCount: 0 },
}: SignupFormProps) {
  const modals = useModals();
  const {
    startDateInFuture,
    formattedStartDate,
    totals,
    showOverages,
    customerOverage,
    objectOverage,
    isMonthlyInterval,
    newPipelinesObjectsPricing,
  } = useSignupPricing(plan, coupon);

  // Refresh the tax rate as the billing address changes (clears until complete). `isPending` blocks
  // submit so the charge can never run against a total priced for the previous address.
  const taxRate = useTaxRateRefresh();

  const checkoutOptions = buildCheckoutOptions({
    coupon,
    couponCode,
    isAllowedToUseCoupon,
    isPriceLoading,
    startDateInFuture,
    isMonthlyInterval,
    totals,
    canCancel,
    cancelActionLabel,
    onApplyCoupon,
    onBillingAddressChange: taxRate.onAddressChange,
    isTaxRateLoading: taxRate.isPending,
  });

  return (
    <Box p="400">
      <SignupHeader
        plan={plan}
        startDateInFuture={startDateInFuture}
        formattedStartDate={formattedStartDate}
        planPrice={totals.planPrice}
        showOverages={showOverages}
        usage={usage}
        customerOverage={customerOverage}
        objectOverage={objectOverage}
        onWhatsIncluded={() => modals.open(PlanIncludedModal, { plan, newPipelinesObjectsPricing })}
      />

      <CreditCardForm
        submitButtonText={startDateInFuture ? 'Continue' : 'Pay now'}
        onSubmit={startSubscription}
        checkout={checkoutOptions}
      />
    </Box>
  );
}
